<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="admin-page">
    <div class="admin-header">
      <div>
        <h1 class="admin-title">✉️ 聯絡表單</h1>
        <p class="admin-sub">管理所有客戶詢問</p>
      </div>
      <div class="header-stats">
        <span class="stat-badge"> 未讀：{{ unreadCount }} 封 </span>
      </div>
    </div>

    <div class="admin-content">
      <div v-if="loading" class="loading">載入中...</div>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>狀態</th>
            <th>姓名</th>
            <th>郵件</th>
            <th>電話</th>
            <th>類別</th>
            <th>詢問內容</th>
            <th>時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in contacts" :key="c.id" :class="{ unread: !c.isRead }">
            <td>
              <span
                :class="['status', c.isRead ? 'status-read' : 'status-unread']"
              >
                {{ c.isRead ? "已讀" : "未讀" }}
              </span>
            </td>
            <td class="td-name">{{ c.name }}</td>
            <td class="td-email">{{ c.email }}</td>
            <td class="td-phone">{{ c.phone || "—" }}</td>
            <td class="td-type">{{ c.type }}</td>
            <td class="td-message">{{ c.message }}</td>
            <td class="td-date">{{ formatDate(c.createdAt) }}</td>
            <td class="td-actions">
              <button v-if="!c.isRead" class="btn-read" @click="markAsRead(c)">
                標為已讀
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁 -->
      <div v-if="!loading && totalPages > 1" class="pagination">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
        >
          ← 上一頁
        </button>
        <span class="page-info"
          >第 {{ currentPage }} / {{ totalPages }} 頁（共
          {{ totalCount }} 筆）</span
        >
        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)"
        >
          下一頁 →
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";

const contacts = ref([]);
const loading = ref(true);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const pageSize = 20;

// 未讀數量：目前頁面看到的未讀（如果想要「全站未讀總數」需要另外的 API，先以當前頁計算）
const unreadCount = computed(
  () => contacts.value.filter((c) => !c.isRead).length
);

const loadContacts = async (page = 1) => {
  loading.value = true;
  try {
    const response = await axios.get("/contact/list", {
      params: { page, pageSize },
    });
    contacts.value = response.data.items;
    currentPage.value = response.data.page;
    totalPages.value = response.data.totalPages;
    totalCount.value = response.data.totalCount;
  } catch (error) {
    console.error("載入失敗:", error);
  } finally {
    loading.value = false;
  }
};

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value) return;
  loadContacts(page);
};

const markAsRead = async (contact) => {
  try {
    await axios.put(`/contact/${contact.id}/read`);
    contact.isRead = true;
  } catch (error) {
    console.error("更新失敗:", error);
  }
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString("zh-TW");
};

onMounted(() => {
  loadContacts(1);
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.td-phone {
  color: #6b7f68;
  white-space: nowrap;
}
.admin-page {
  padding: 40px;
  font-family: "Noto Sans TC", "Microsoft JhengHei", sans-serif;
}
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}
.admin-title {
  font-size: 1.8em;
  color: #1c2b1a;
  margin-bottom: 4px;
}
.admin-sub {
  color: #6b7f68;
  font-size: 14px;
}
.stat-badge {
  background: #ff6b6b;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}
.admin-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.admin-table {
  width: 100%;
  border-collapse: collapse;
}
.admin-table th {
  background: #f5f7fa;
  padding: 14px 16px;
  text-align: left;
  font-size: 13px;
  color: #6b7f68;
  font-weight: 600;
  border-bottom: 1px solid #eee;
}
.admin-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  font-size: 14px;
}
.admin-table tr:last-child td {
  border-bottom: none;
}
.admin-table tr.unread td {
  background: #fffbf0;
}
.admin-table tr:hover td {
  background: #f9fafb;
}
.status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.status-unread {
  background: #fff3cd;
  color: #856404;
}
.status-read {
  background: #eaf2e0;
  color: #4e7b4f;
}
.td-name {
  font-weight: 600;
  color: #1c2b1a;
}
.td-email {
  color: #667eea;
}
.td-type {
  color: #6b7f68;
}
.td-message {
  color: #3d4f3a;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.td-date {
  color: #9baa97;
  font-size: 12px;
  white-space: nowrap;
}
.td-actions {
  white-space: nowrap;
}
.btn-read {
  padding: 6px 14px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}
.btn-read:hover {
  background: #2d5016;
}
.loading {
  padding: 60px;
  text-align: center;
  color: #9baa97;
}

/* 分頁 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 20px;
  border-top: 1px solid #eee;
}
.page-btn {
  padding: 8px 18px;
  background: #f5f7fa;
  color: #4e7b4f;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}
.page-btn:hover:not(:disabled) {
  background: #eaf2e0;
  border-color: #4e7b4f;
}
.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.page-info {
  font-size: 13px;
  color: #6b7f68;
}
</style>
